import React from 'react';
import { motion } from 'framer-motion';
import { Play, Camera, Sparkles } from 'lucide-react';
import VideoBackground from '../VideoBackground';
import aiBrainVideo from '@/assets/ai-brain-video.jpg';

interface Step1LandingProps {
  onNext: () => void;
}

const Step1Landing: React.FC<Step1LandingProps> = ({ onNext }) => {
  const fallbackBackground = (
    <div className="absolute inset-0">
      <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900/30 to-black"></div>
      <motion.div 
        className="absolute top-0 left-0 w-96 h-96 bg-gray-500/20 rounded-full blur-3xl"
        animate={{ 
          x: [0, 100, 0],
          y: [0, 50, 0],
          scale: [1, 1.2, 1]
        }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div 
        className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"
        animate={{ 
          x: [0, -100, 0],
          y: [0, -50, 0],
          scale: [1.2, 1, 1.2]
        }}
        transition={{ duration: 10, repeat: Infinity }}
      />
    </div>
  );

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col">
      <VideoBackground
        videoSrc="https://res.cloudinary.com/drinkamrs/video/upload/v1752753031/Characters_wole_ai_02_a_202507170450_g9mto0.mp4"
        fallbackComponent={fallbackBackground}
        overlay={true}
      />
      {/* Header */}
      <div className="relative z-10 p-4 text-center">
        <motion.div 
          className="flex items-center justify-center gap-2"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <img 
            src="/lovable-uploads/120101b5-df74-479f-bfcb-7643f8fd8af8.png" 
            alt="LovaBuild" 
            className="h-10 w-auto"
          />
          <span className="text-xl font-bold bg-gradient-to-r from-gray-400 to-white bg-clip-text text-transparent">LovaBuild</span>
        </motion.div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 py-4">
        {/* Compact Visual */}
        <motion.div 
          className="relative mb-4"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
        >
          <div className="relative w-32 h-32 mx-auto">
            {/* Outer ring */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-hollywood-gold/30 to-hollywood-gold-light/30 rounded-full blur-lg"
              animate={{ 
                scale: [1, 1.2, 1],
                rotate: 360
              }}
              transition={{ 
                scale: { duration: 3, repeat: Infinity },
                rotate: { duration: 8, repeat: Infinity, ease: "linear" }
              }}
            />
            
            {/* Core with camera */}
            <motion.div
              className="absolute inset-4 bg-gradient-to-br from-hollywood-gold via-hollywood-gold-light to-hollywood-shimmer rounded-full flex items-center justify-center shadow-2xl"
              animate={{ 
                rotateY: [0, 360]
              }}
              transition={{ 
                duration: 4, 
                repeat: Infinity,
                ease: "linear"
              }}
            >
              <Camera className="w-8 h-8 text-white" />
            </motion.div>
            
            {/* Floating elements */}
            <motion.div
              className="absolute -top-2 -right-2"
              animate={{ 
                y: [-5, 5, -5],
                rotate: 360
              }}
              transition={{ 
                y: { duration: 2, repeat: Infinity },
                rotate: { duration: 3, repeat: Infinity, ease: "linear" }
              }}
            >
              <div className="bg-gradient-to-r from-hollywood-gold to-hollywood-gold-light p-1.5 rounded-full">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Headline */}
        <motion.h1 
          className="text-2xl md:text-4xl lg:text-5xl font-bold text-center text-white mb-4 leading-tight"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <span className="bg-gradient-to-r from-hollywood-gold via-hollywood-gold-light to-hollywood-shimmer bg-clip-text text-transparent">
            Unleash Your Inner Filmmaker
          </span>
          <br />
          <span className="text-white text-xl md:text-3xl lg:text-4xl">
            AI Creates Amazing Videos for You
          </span>
        </motion.h1>

        {/* Body Text */}
        <motion.p 
          className="text-base md:text-lg text-gray-300 text-center mb-8 max-w-md leading-relaxed px-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          Generate <span className="text-hollywood-gold font-semibold">stunning videos</span>, <span className="text-hollywood-shimmer font-semibold">captivating movies</span>, and <span className="text-hollywood-gold-light font-semibold">powerful video ads</span> with AI - no complex editing required.
        </motion.p>

        {/* CTA Button */}
        <motion.button
          className="relative bg-gradient-to-r from-hollywood-gold-dark via-hollywood-gold to-hollywood-gold-light text-white px-6 py-3 rounded-full font-semibold text-base shadow-2xl hover:shadow-hollywood-shimmer/25 transition-all duration-300 overflow-hidden group"
          onClick={onNext}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-hollywood-shimmer via-hollywood-gold-light to-hollywood-shimmer opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          />
          <span className="relative z-10">Start Creating Now</span>
        </motion.button>
      </div>

      {/* Footer */}
      <div className="relative z-10 p-4 text-center text-xs text-gray-400">
        <div className="flex flex-wrap justify-center gap-4 mb-2">
          <a href="#" className="hover:text-gray-300 transition-colors">
            Terms & Conditions
          </a>
          <a href="#" className="hover:text-gray-300 transition-colors">
            Terms & Conditions
          </a>
        </div>
        <p>© 2024 iSelect Digital. All rights reserved.</p>
      </div>
    </div>
  );
};

export default Step1Landing;